import React from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Upload, Trash2, FileSpreadsheet } from 'lucide-react';
import { useStatementImports, useDeleteStatementImport } from '@/hooks/finance/useStatementImports';
import { useBankAccounts } from '@/hooks/use-bank-accounts';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const getFileTypeLabel = (fileFormat?: string | null) => {
  if (!fileFormat) return "Unknown";

  const v = fileFormat.toLowerCase();

  // MIME → label
  if (v.includes("csv")) return "CSV";
  if (v.includes("tsv") || v.includes("tab-separated")) return "TSV";
  if (
    v.includes("spreadsheet") ||
    v.includes("excel") ||
    v.includes("sheet") ||
    v.includes("xlsx") ||
    v.includes("application/vnd.ms-excel")
  ) return "XLSX";

  // fallback: если вдруг там уже расширение
  if (v === "csv") return "CSV";
  if (v === "tsv") return "TSV";
  if (v === "xlsx") return "XLSX";

  // если прилетит что-то странное — покажем как есть
  return fileFormat;
};


const ImportHistoryPage = () => {
  const navigate = useNavigate();
  const { data: imports = [], isLoading } = useStatementImports();
  const { data: bankAccounts = [] } = useBankAccounts();
  const deleteImport = useDeleteStatementImport();

  const getAccountName = (accountId: string) => {
    const account = bankAccounts.find(a => a.id === accountId);
    return account?.name || 'Unknown';
  };

  const handleDelete = async (id: string) => {
    await deleteImport.mutateAsync(id);
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Import History</h1>
            <p className="text-muted-foreground">
              View all bank statement imports
            </p>
          </div>
          <Button onClick={() => navigate('/finance/banking/transactions')}>
            <Upload className="h-4 w-4 mr-2" />
            Import Statement
          </Button>
        </div>

        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Account</TableHead>
                <TableHead>File</TableHead>
                <TableHead>Format</TableHead>
                <TableHead>Date Range</TableHead>
                <TableHead className="text-center">Transactions</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[80px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    Loading import history...
                  </TableCell>
                </TableRow>
              ) : imports.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    <FileSpreadsheet className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
                    <p>No imports yet. Import a bank statement to get started.</p>
                  </TableCell>
                </TableRow>
              ) : (
                imports.map((imp) => (
                  <TableRow key={imp.id}>
                    <TableCell className="whitespace-nowrap">
                      {format(new Date(imp.created_at), 'dd MMM yyyy HH:mm')}
                    </TableCell>
                    <TableCell>{getAccountName(imp.bank_account_id)}</TableCell>
                    <TableCell className="max-w-[200px] truncate">
                      {imp.file_name}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{getFileTypeLabel(imp.file_format)}</Badge>
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {imp.date_from && imp.date_to ? (
                        `${format(new Date(imp.date_from), 'dd MMM')} - ${format(new Date(imp.date_to), 'dd MMM yyyy')}`
                      ) : '-'}
                    </TableCell>
                    <TableCell className="text-center">
                      {imp.transaction_count || 0}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={imp.status === 'completed' ? 'default' : 'destructive'}
                      >
                        {imp.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete Import</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will delete the import record and all {imp.transaction_count || 0} associated transactions. This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDelete(imp.id)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </Layout>
  );
};

export default ImportHistoryPage;
